import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { handleApiError, hydrateFlowFromParsedJson } from '../utils';
import { MOCK_DATA, NODE_TYPE_CONFIGS } from '../constants';

// 數據獲取管理 Hook
export const useDataFetching = (workflowIdOverride) => {
  const [searchParams] = useSearchParams();
  const workflowId = workflowIdOverride ?? searchParams.get('id');
  
  
  // 工作流程基本信息
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [updatedAt, setUpdatedAt] = useState('');
  const [createdBy, setCreatedBy] = useState('');
  const [updatedBy, setUpdatedBy] = useState('');
  const [status, setStatus] = useState('');
  
  // 模板相關
  const [templates, setTemplates] = useState([]);
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // 用戶相關
  const [users, setUsers] = useState([]);
  const [isUserModalVisible, setIsUserModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // EForm 相關
  const [eforms, setEforms] = useState([]);
  const [isEFormModalVisible, setIsEFormModalVisible] = useState(false);
  const [selectedEForm, setSelectedEForm] = useState(null);
  
  // 收件人相關
  const [isRecipientModalVisible, setIsRecipientModalVisible] = useState(false);
  
  // 工作流程圖載入中（凍結畫布、防止誤點）
  const [isWorkflowLoading, setIsWorkflowLoading] = useState(false);
  
  // 節點類型定義
  const [nodeTypeDefinitions, setNodeTypeDefinitions] = useState([]);
  const [loadingNodeTypes, setLoadingNodeTypes] = useState(true);
  
  // 流程變量相關
  const [processVariables, setProcessVariables] = useState([]);
  const [processVariablesModalVisible, setProcessVariablesModalVisible] = useState(false);
  const [selectedProcessVariable, setSelectedProcessVariable] = useState(null);
  const [editingProcessVariable, setEditingProcessVariable] = useState(null);

  // 上次載入/保存的流程 JSON（用於判斷是否有未保存變更）
  const [lastLoadedFlowJson, setLastLoadedFlowJson] = useState('');
  
  // 獲取模板列表
  const fetchTemplates = useCallback(async () => {
    try {
      const templates = await apiService.fetchTemplates();
      // apiService.fetchTemplates() 已經處理了錯誤情況
      // 如果網絡連接失敗，會返回 MOCK_DATA.templates
      // 如果 API 正常返回空數據，會返回 []
      setTemplates(templates || []);
    } catch (error) {
      // 只有在真正的未預期錯誤時才使用 mock data
      // 但 apiService 已經處理了大部分情況，這裡應該很少執行到
      console.error('獲取模板列表未預期錯誤:', error);
      // 只有在網絡連接失敗時才使用 mock data
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.warn('⚠️ 網絡連接失敗，使用模擬數據作為後備');
        setTemplates(MOCK_DATA.templates);
      } else {
        // 其他錯誤，返回空數組
        setTemplates([]);
      }
    }
  }, []);

  // 獲取 Meta 模板列表
  const fetchMetaTemplates = useCallback(async () => {
    try {
      const metaTemplates = await apiService.fetchMetaTemplates();
      setMetaTemplates(metaTemplates);
    } catch (error) {
      console.error('獲取 Meta 模板列表錯誤:', error);
      setMetaTemplates([]);
    }
  }, []);

  // 獲取用戶列表
  const fetchUsers = useCallback(async () => {
    try {
      const users = await apiService.fetchUsers();
      setUsers(users);
    } catch (error) {
      handleApiError(error, MOCK_DATA.users, setUsers, '獲取用戶列表錯誤');
    }
  }, []);

  // 獲取 EForm 列表
  const fetchEForms = useCallback(async () => {
    try {
      const eforms = await apiService.fetchEForms();
      setEforms(eforms);
    } catch (error) {
      handleApiError(error, MOCK_DATA.eforms, setEforms, '獲取 EForm 列表錯誤');
    }
  }, []);

  // 獲取節點類型定義
  const fetchNodeTypeDefinitions = useCallback(async (nodeTypes) => {
    try {
      setLoadingNodeTypes(true);
      const definitions = await apiService.fetchNodeTypeDefinitions();
      if (definitions) {
        // 將後端數據與本地翻譯合併（聯集）
        // 避免前端新增節點但後端 definitions 尚未同步時，Sidebar 看不到節點。
        const backendTypeSet = new Set(definitions.map(d => d.type));

        const mergedBackend = definitions.map(backendNodeType => {
          const localNodeType = nodeTypes.find(nt => nt.type === backendNodeType.type);
          const configNodeType = NODE_TYPE_CONFIGS.find(nt => nt.type === backendNodeType.type);
          return {
            ...backendNodeType,
            label: localNodeType ? localNodeType.label : backendNodeType.label,
            iconColor: configNodeType?.iconColor || null,
            ...(configNodeType?.hidden && { hidden: true }),
          };
        });

        const localOnlyTypes = nodeTypes
          .filter(nt => !backendTypeSet.has(nt.type))
          .map(nt => {
            const configNodeType = NODE_TYPE_CONFIGS.find(cfg => cfg.type === nt.type);
            return {
              type: nt.type,
              label: nt.label,
              category: configNodeType?.category || 'Default',
              description: configNodeType?.description || nt.label,
              isImplemented: configNodeType?.isImplemented ?? true,
              hasExecution: configNodeType?.hasExecution ?? (nt.type !== 'start' && nt.type !== 'end'),
              iconColor: configNodeType?.iconColor || null,
              defaultData: {},
              ...(configNodeType?.hidden && { hidden: true }),
            };
          });

        const mergedData = [...mergedBackend, ...localOnlyTypes];
        setNodeTypeDefinitions(mergedData);
      } else {
        setNodeTypeDefinitions(nodeTypes.map(nt => {
          const cfg = NODE_TYPE_CONFIGS.find(c => c.type === nt.type);
          return {
            type: nt.type,
            label: nt.label,
            category: cfg?.category || 'Default',
            description: cfg?.description || nt.label,
            isImplemented: cfg?.isImplemented ?? true,
            hasExecution: cfg?.hasExecution ?? (nt.type !== 'start' && nt.type !== 'end'),
            iconColor: cfg?.iconColor || null,
            defaultData: {},
          };
        }));
      }
    } catch (error) {
      console.error('獲取節點類型定義錯誤:', error);
      setNodeTypeDefinitions(nodeTypes.map(nt => {
        const cfg = NODE_TYPE_CONFIGS.find(c => c.type === nt.type);
        return {
          type: nt.type,
          label: nt.label,
          category: cfg?.category || 'Default',
          description: cfg?.description || nt.label,
          isImplemented: cfg?.isImplemented ?? true,
          hasExecution: cfg?.hasExecution ?? (nt.type !== 'start' && nt.type !== 'end'),
          iconColor: cfg?.iconColor || null,
          defaultData: {},
        };
      }));
    } finally {
      setLoadingNodeTypes(false);
    }
  }, []);

  // 獲取流程變量
  const fetchProcessVariables = useCallback(async () => {
    if (!workflowId) return;
    
    try {
      const variables = await apiService.fetchProcessVariables(workflowId);
      setProcessVariables(variables);
    } catch (error) {
      console.error('獲取流程變量失敗:', error);
      setProcessVariables([]);
    }
  }, [workflowId]);

  // 載入現有流程資料，回傳載入的 json 字串（供判斷未保存變更）
  const loadWorkflow = useCallback(async (setNodes, setEdges, nodeTypes, handleDeleteNode, onEdgeSwitch) => {
    if (!workflowId) return '';
    
    try {
      const data = await apiService.fetchWorkflowDefinition(workflowId);
      if (data == null) return '';
      setName(data.name || '');
      setDescription(data.description || '');
      setCreatedAt(data.createdAt || '');
      setUpdatedAt(data.updatedAt || '');
      setCreatedBy(data.createdBy || '');
      setUpdatedBy(data.updatedBy || '');
      setStatus(data.status || 'Enabled');
      
      if (data.json) {
        try {
          const flow = JSON.parse(data.json);
          if (flow.nodes) {
            const { nodes: hydratedNodes, edges: hydratedEdges } = hydrateFlowFromParsedJson(
              flow,
              nodeTypes,
              handleDeleteNode,
              onEdgeSwitch
            );
            setNodes(hydratedNodes);
            setEdges(hydratedEdges);
          }
          setLastLoadedFlowJson(data.json);
          return data.json;
        } catch (parseError) {
          console.error('解析流程 JSON 失敗:', parseError);
        }
      }
      return '';
    } catch (error) {
      console.error('載入流程定義失敗:', error);
      throw error;
    }
  }, [workflowId]);

  // 初始化數據
  const initializeData = useCallback(async (nodeTypes, setNodes, setEdges, handleDeleteNode, onEdgeSwitch) => {
    setStatus('');
    setIsWorkflowLoading(true);
    try {
      await fetchNodeTypeDefinitions(nodeTypes);
      await fetchTemplates();
      await fetchMetaTemplates();
      await fetchUsers();
      await fetchEForms();
      
      if (workflowId) {
        await fetchProcessVariables();
        await loadWorkflow(setNodes, setEdges, nodeTypes, handleDeleteNode, onEdgeSwitch);
      }
    } finally {
      setIsWorkflowLoading(false);
    }
  }, [workflowId, fetchNodeTypeDefinitions, fetchTemplates, fetchMetaTemplates, fetchUsers, fetchEForms, fetchProcessVariables, loadWorkflow]);

  return {
    // 工作流程基本信息
    name,
    setName,
    description,
    setDescription,
    createdAt,
    setCreatedAt,
    updatedAt,
    setUpdatedAt,
    createdBy,
    setCreatedBy,
    updatedBy,
    setUpdatedBy,
    status,
    setStatus,
    workflowId,
    
    // 模板相關
    templates,
    setTemplates,
    metaTemplates,
    setMetaTemplates,
    isTemplateModalVisible,
    setIsTemplateModalVisible,
    selectedTemplate,
    setSelectedTemplate,
    
    // 用戶相關
    users,
    setUsers,
    isUserModalVisible,
    setIsUserModalVisible,
    selectedUser,
    setSelectedUser,
    
    // EForm 相關
    eforms,
    setEforms,
    isEFormModalVisible,
    setIsEFormModalVisible,
    selectedEForm,
    setSelectedEForm,
    
    // 節點類型定義
    nodeTypeDefinitions,
    setNodeTypeDefinitions,
    loadingNodeTypes,
    setLoadingNodeTypes,
    
    // 流程變量相關
    processVariables,
    setProcessVariables,
    processVariablesModalVisible,
    setProcessVariablesModalVisible,
    selectedProcessVariable,
    setSelectedProcessVariable,
    editingProcessVariable,
    setEditingProcessVariable,
    
    // 收件人相關
    isRecipientModalVisible,
    setIsRecipientModalVisible,
    
    // 工作流程圖載入狀態（用於凍結畫布、顯示 loading）
    isWorkflowLoading,
    
    // 上次載入/保存的流程 JSON（用於未保存變更提示）
    lastLoadedFlowJson,
    setLastLoadedFlowJson,

    // 數據獲取函數
    fetchTemplates,
    fetchMetaTemplates,
    fetchUsers,
    fetchEForms,
    fetchNodeTypeDefinitions,
    fetchProcessVariables,
    loadWorkflow,
    initializeData,
  };
};
